import React, { useEffect, useState } from 'react';

import { Container, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { getPath } from 'routes';
import { ArrowPointerLeftIcon } from 'assets/icons';
import { GroupManagementTabs } from 'constants/enums';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';

import ActionsMenu from './ActionsMenu';
import {
  Baseline,
  Goals,
  Observations,
  EducationalContents,
  SmartEvents,
  Messages,
  Questionnaires,
  ReferenceRanges,
  Title,
  Tasks
} from './blocks';
import Descriptions from './blocks/Descriptions';

interface ICarePlanEditorProps {
  carePlan?: ICarePlan | undefined;
  template?: ICarePlanTemplate | undefined;
  getCarePlan: () => any;
  loading: boolean;
  mode: 'template' | 'carePlan';
  ccmOnly?: boolean;
}

const CarePlanEditor = ({
  carePlan,
  template,
  getCarePlan,
  loading,
  mode,
  ccmOnly = false
}: ICarePlanEditorProps) => {
  const intl = useIntl();
  const [_carePlan, setCarePlan] = useState(carePlan);
  const [_template, setTemplate] = useState(template);

  const isTemplatePrivate = _template?.template_type === 'PRIVATE';
  const [isPublished, setIsPublished] = useState<boolean>(_template?.status === '1');

  const hasCarePlan = !!(
    _carePlan?.carePlan.goals.length ||
    _carePlan?.educationalContent.length ||
    _carePlan?.messages.length ||
    _carePlan?.observationTasks.length ||
    _carePlan?.questionnaires.length ||
    _carePlan?.referenceRanges.length ||
    _carePlan?.scheduled.length ||
    _carePlan?.smartEvents.length
  );

  const [{ loading: postLoading, error: postError }, postUpdate] = useAxios(
    {
      url: '/clinician/2.0.0/CarePlanTemplate',
      method: 'POST'
    },
    { manual: true }
  );

  const updateCarePlanTemplate = async (carePlanTemplate: ICarePlanTemplate) => {
    await postUpdate({
      data: {
        carePlanTemplate
      }
    });
    getCarePlan();
  };

  const handlePublish = async () => {
    if (_template) {
      const _isPublish = isPublished;
      setIsPublished(!_isPublish);
      const templateUpdated = {
        ..._template,
        status: isPublished ? '0' : '1'
      };
      try {
        await updateCarePlanTemplate(templateUpdated);
      } catch (err) {
        setIsPublished(_isPublish);
      }
    }
  };

  useEffect(() => {
    setCarePlan(carePlan);
    setTemplate(template);
  }, [carePlan, template]);

  useEffect(() => {
    setIsPublished(_template?.status === '1');
  }, [_template?.status]);

  return (
    <Container
      fluid
      id="carePlan"
      className="pb-3"
      data-test="care-plan-template-editor"
      data-test-value={_template?.template_id || 'new'}
    >
      {mode === 'carePlan' && (
        <div className="mb-4 d-flex justify-content-end">
          <ActionsMenu getCarePlan={getCarePlan} hasCarePlan={hasCarePlan} />
        </div>
      )}
      {mode === 'template' && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button variant="link" className="ps-0">
            <Link
              to={getPath().dashboard.groupManagement.layout(GroupManagementTabs.CARE_PLAN)}
              className="d-flex align-items-center"
              data-test="care-plan-templates-goto-list-btn"
            >
              <ArrowPointerLeftIcon className="icon me-2" />
              {intl.formatMessage({ id: 'backToTemplatesKey' })}
            </Link>
          </Button>

          {isTemplatePrivate && (
            <Button variant="link" className="border-0 shadow-none d-flex" onClick={handlePublish}>
              <Form.Check type="switch" checked={isPublished} readOnly />
              {intl.formatMessage({ id: isPublished ? 'publishedKey' : 'unpublishKey' })}
            </Button>
          )}
        </div>
      )}
      {(postLoading || loading) && <Loading />}
      {postError && <Error />}
      {mode === 'template' && (
        <Title
          title={_template?.template_description || ''}
          template_id={_template?.template_id || 'new'}
          carePlanTemplate={_template}
          onFinishUpdate={getCarePlan}
        />
      )}

      {mode === 'carePlan' && (
        <>
          <Descriptions
            descriptions={_carePlan?.carePlan.descriptions || []}
            care_plan_id={_carePlan?.carePlan.care_plan_id}
            getCarePlan={getCarePlan}
          />
          <Goals
            goals={_carePlan?.carePlan.goals || _template?.carePlan.goals || []}
            care_plan_id={_carePlan?.carePlan.care_plan_id}
            getCarePlan={getCarePlan}
          />
        </>
      )}
      {!ccmOnly && <Baseline mode={mode} carePlan={_carePlan} carePlanTemplate={_template} />}
      {!ccmOnly && (
        <ReferenceRanges
          referenceRanges={_carePlan?.referenceRanges || _template?.referenceRanges}
          getCarePlan={getCarePlan}
          carePlanTemplate={_template}
        />
      )}
      <SmartEvents
        smartAlerts={_carePlan?.smartEvents || _template?.smartEvents}
        getCarePlan={getCarePlan}
        carePlanTemplate={_template}
      />
      {!ccmOnly && (
        <Observations
          observations={_carePlan?.observationTasks || _template?.observationTasks}
          getCarePlan={getCarePlan}
          carePlanTemplate={_template}
        />
      )}
      <Tasks carePlanTemplate={_template} carePlan={carePlan} />
      <Messages
        messages={_carePlan?.messages || _template?.messages}
        getCarePlan={getCarePlan}
        carePlanTemplate={_template}
      />
      <EducationalContents
        educationalContents={_carePlan?.educationalContent || _template?.educationalContent}
        getCarePlan={getCarePlan}
        carePlanTemplate={_template}
      />
      <Questionnaires
        questionnaires={_carePlan?.questionnaires || _template?.questionnaires}
        getCarePlan={getCarePlan}
        carePlanTemplate={_template}
      />
    </Container>
  );
};

export default CarePlanEditor;
